/**
 * usePageHero — хук для получения CMS hero-блоков страницы.
 * v6: мгновенный рендер без задержки.
 *
 * Логика:
 *  1. Синхронно читает из localStorage-кеша (preloadHeroFromStorage в globalDataCache)
 *  2. Если localStorage свежий (< 5 минут) — сетевой запрос не нужен
 *  3. Если кеша нет — сразу показывает fallbacks (переводы), фоновый fetch не блокирует UI
 *  4. Когда данные приходят — подменяет fallbacks на CMS-значения без мигания
 *  5. При обновлении из админки — реактивно обновляет через onCmsUpdate
 */

import { useState, useEffect, useRef } from 'react';
import { getCachedHero, fetchHeroTexts, subscribeToCache } from '@/lib/globalDataCache';
import { onCmsUpdate } from '@/lib/cms';
import { HeroTexts } from '@/lib/cms-hero';

interface UsePageHeroOptions {
  pageKey: string;
  lang: string;
  /** Используются только если в DB нет записи вообще */
  fallbacks?: {
    eyebrow?: string;
    title?: string;
    subtitle?: string;
  };
}

interface PageHeroResult extends HeroTexts {
  loaded: boolean;
}

export function usePageHero({ pageKey, lang, fallbacks = {} }: UsePageHeroOptions): PageHeroResult {
  const hasFallbacks = !!(fallbacks.eyebrow || fallbacks.title || fallbacks.subtitle);

  // Compute hero texts for the current (pageKey, lang) synchronously.
  // Order: cache first (fresh data), then fallbacks (translations).
  const initialFor = (k: string, l: string): HeroTexts => {
    const cached = getCachedHero(k, l);
    if (cached) {
      return {
        eyebrow: cached.eyebrow !== '' ? cached.eyebrow : (fallbacks.eyebrow ?? ''),
        title: cached.title !== '' ? cached.title : (fallbacks.title ?? ''),
        subtitle: cached.subtitle !== '' ? cached.subtitle : (fallbacks.subtitle ?? ''),
      };
    }
    return {
      eyebrow: fallbacks.eyebrow ?? '',
      title: fallbacks.title ?? '',
      subtitle: fallbacks.subtitle ?? '',
    };
  };

  const [data, setData] = useState<HeroTexts>(() => initialFor(pageKey, lang));
  const [loaded, setLoaded] = useState(() => getCachedHero(pageKey, lang) !== null || hasFallbacks);

  // CRITICAL: when (pageKey, lang) changes, reset state synchronously DURING render
  // so the next paint shows the correct language immediately — no flicker through
  // the previous language. React 18 supports this pattern when the new state is
  // derived from props (here: pageKey/lang).
  const keyRef = useRef<string>(`${pageKey}:${lang}`);
  const currentKey = `${pageKey}:${lang}`;
  if (keyRef.current !== currentKey) {
    keyRef.current = currentKey;
    setData(initialFor(pageKey, lang));
    setLoaded(getCachedHero(pageKey, lang) !== null || hasFallbacks);
  }

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Check cache again in case it was populated between render and effect
    const cached = getCachedHero(pageKey, lang);
    if (cached) {
      setData(cached);
      setLoaded(true);
    }

    // Fetch (will use cache if fresh, otherwise fetches from DB)
    fetchHeroTexts(pageKey, lang, fallbacks).then((result) => {
      if (!mountedRef.current) return;
      setData({
        eyebrow: result.eyebrow !== '' ? result.eyebrow : (fallbacks.eyebrow ?? ''),
        title: result.title !== '' ? result.title : (fallbacks.title ?? ''),
        subtitle: result.subtitle !== '' ? result.subtitle : (fallbacks.subtitle ?? ''),
      });
      setLoaded(true);
    });

    // Subscribe to global cache updates
    const unsubscribe = subscribeToCache(() => {
      const updated = getCachedHero(pageKey, lang);
      if (updated && mountedRef.current) {
        setData({
          eyebrow: updated.eyebrow !== '' ? updated.eyebrow : (fallbacks.eyebrow ?? ''),
          title: updated.title !== '' ? updated.title : (fallbacks.title ?? ''),
          subtitle: updated.subtitle !== '' ? updated.subtitle : (fallbacks.subtitle ?? ''),
        });
        setLoaded(true);
      }
    });

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, lang]);

  // Subscribe to CMS updates for instant reflection after admin saves
  useEffect(() => {
    return onCmsUpdate((updPageKey, blockKey, updLang, value) => {
      if (updPageKey !== pageKey || updLang !== lang) return;
      if (blockKey === 'hero.eyebrow') setData((d) => ({ ...d, eyebrow: value }));
      else if (blockKey === 'hero.title') setData((d) => ({ ...d, title: value }));
      else if (blockKey === 'hero.subtitle') setData((d) => ({ ...d, subtitle: value }));
    });
  }, [pageKey, lang]);

  return { ...data, loaded };
}
